import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import cn from 'classnames';
import eventTracking from 'services/analytics/eventTracking';

import { getLocale } from '../../lib/i18n/utils';

import { Nullable } from '../../types/general';

import { getAuthFeatureFlags } from '../../selectors/authFeatureFlags.selectors';

import { useUrlQueryParam } from '../../hooks/useUrlQueryParam';
import * as navigationService from '../../lib/navigation';
import auth from '../../services/auth';
import { useRegistrationRedirectLink } from '../registration/hooks/useRegistrationRedirectLink';
import { useRegistrationSource } from '../registration/hooks/useRegistrationSource';
import { RegistrationMethod } from '../registration/hooks/useRegistrationSuccessEffect';
import { useVerifiedInviteTokenData } from '../registration/hooks/useVerifiedInviteTokenData';

import css from './GoogleAuthButton.css';

interface CredentialResponse {
    /**
     * the ID token as a base64-encoded JSON Web Token (JWT) string.
     */
    credential: string;

    /**
     * This field sets how the credential is selected.
     */
    select_by: string;
}
interface GoogleAuthButtonProps {
    theme?: 'outline' | 'filled_blue' | 'filled_black';
    type: 'standard' | 'icon';
    shape?: 'rectangular' | 'pill' | 'circle' | 'square';
    size?: 'small' | 'medium' | 'large';
    locale?: string;
    onLogin?: (
        nextUrl: string,
        is2FAEnabled?: boolean,
        method?: RegistrationMethod,
    ) => void;
    onRegister?: (
        nextUrl: string,
        email: string,
        registrationMethod?: RegistrationMethod,
    ) => void;
    errorHandler?: (error: { code: string; reason: string }) => void;
}

/*
   This React component is a "Sign up with Google" button, which enables the user to register using their
   Google account. If the user is already registered, then we just log in user into the account
 */
const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({
    theme = 'outline',
    size,
    type,
    shape,
    onLogin = () => undefined,
    onRegister = () => undefined,
    errorHandler = () => undefined,
}) => {
    const googleButton = useRef<Nullable<HTMLDivElement>>(null);
    const referrer = useUrlQueryParam('ref') || '';
    const activeProTrial = useUrlQueryParam('activeProTrial');
    const source = useRegistrationSource();
    const redirect = useRegistrationRedirectLink();

    const authFeatureFlags = useSelector(getAuthFeatureFlags);

    const { email: invite_email } = useVerifiedInviteTokenData();

    async function handleCredentialResponse(response: CredentialResponse) {
        try {
            const {
                nextUrl,
                is2FAEnabled,
                flow,
                email,
            } = await auth.googleAuth({
                token: response.credential,
                source,
                redirect,
                referrer,
                isActiveProTrial: Boolean(activeProTrial),
                invite_email: invite_email || null,
            });

            switch (flow) {
                case 'login': {
                    onLogin(nextUrl, is2FAEnabled, 'google');
                    break;
                }
                case 'registration':
                    eventTracking.sendToGTM({
                        event: 'funnel_reg_step_2_google',
                        category: '',
                        action: '',
                        label: '',
                    });
                    onRegister(nextUrl, email, 'google');
                    break;
                default:
                    navigationService.redirect(nextUrl);
            }
        } catch (error) {
            errorHandler(error);
        }
    }

    useEffect(() => {
        /* global google */

        // Check if the Google object is available in global scope
        if (!window.google) {
            return;
        }
        google?.accounts?.id.initialize({
            client_id: config.GOOGLE_AUTH_CLIENT_ID,
            callback: handleCredentialResponse,
        });
        if (googleButton.current) {
            google?.accounts?.id.renderButton(googleButton.current, {
                theme,
                size,
                type,
                shape,
                logo_alignment: 'center',
                locale: getLocale(),
                width: String(
                    authFeatureFlags.NEW_REGISTRATION_FLOW ? 400 : 380, // The minimum button width, in pixels. The maximum width is 400 pixels
                ),
            });
        }
    }, [googleButton]);

    return (
        <div
            className={cn({
                [css.root]: !authFeatureFlags.NEW_REGISTRATION_FLOW,
            })}
            ref={googleButton}
        />
    );
};

export default GoogleAuthButton;
